import { Component, inject, signal, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';

// Layout Components
import { HeaderComponent } from '../header/header.component';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { FooterComponent } from '../footer/footer.component';

// Services
import { LoadingService } from '@core/services/loading.service';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [CommonModule, RouterOutlet, HeaderComponent, SidebarComponent, FooterComponent],
  template: `
    <div class="dashboard-layout">
      <!-- Mobile Backdrop Overlay -->

      <!-- Header - Hidden on mobile when sidebar is open -->
      <app-header
        class="header-container"
        [class.hidden-mobile]="sidebarOpen() && isMobile()"
        [class.mobile-search-open]="mobileSearchOpen() && isMobile()"
        [class.switcher-open]="companySwitcherOpen()"
        (mobileSearchOpenChange)="mobileSearchOpen.set($event)"
        (sidebarToggle)="toggleSidebar()"
        (companySwitcherOpenChange)="companySwitcherOpen.set($event)"
      >
      </app-header>

      <!-- Main Content Layout -->
      <div class="main-layout">
        <!-- Content Container with Figma-specified rounded corners -->
        <div class="content-container">
          <!-- Sidebar Navigation -->
          <app-sidebar
            class="sidebar-container"
            [class.open]="sidebarOpen()"
            [class.mobile]="isMobile()"
            [isOpen]="sidebarOpen()"
            (toggleSidebar)="toggleSidebar()"
            (closeRequest)="closeSidebar()"
          >
          </app-sidebar>

          <!-- Main Content Area -->
          <div class="main-content" [class.sidebar-open]="sidebarOpen() && !isMobile()">
            <main class="content-area">
              <router-outlet></router-outlet>
            </main>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      :host-context(.dark) {
        .content-container {
          background: var(--bg-card) !important;
        }

        .sidebar-container{
          padding: 10px 0 10px 14px;
        }

        .main-content {
          // border-left: 1px solid var(--colors-border-muted);
        }
      }
      :host-context(body) {
        overflow: hidden !important;
        scrollbar-width: none !important;
        -ms-overflow-style: none !important;
      }

      :host-context(body)::-webkit-scrollbar {
        display: none !important;
        width: 0px !important;
        height: 0px !important;
      }

      :host-context(html) {
        overflow: hidden !important;
        scrollbar-width: none !important;
        -ms-overflow-style: none !important;
      }

      :host {
        display: block;
        height: 100vh;
        overflow: hidden;
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* Internet Explorer 10+ */
      }

      :host::-webkit-scrollbar {
        display: none; /* Hide scrollbar for webkit browsers */
        width: 0px;
        height: 0px;
      }

      .dashboard-layout {
        display: flex;
        flex-direction: column;
        height: 100vh;
        background-color: var(--colors-background-primary);
        transition: var(--component-transition);
        overflow: hidden;
        scrollbar-width: none;
        -ms-overflow-style: none;
      }

      .dashboard-layout::-webkit-scrollbar {
        display: none;
        width: 0px;
        height: 0px;
      }

      .header-container {
        flex-shrink: 0;
        position: relative;
        // z-index: 9999;
      }

      .header-container.switcher-open {
        z-index: 2000;
      }

      .header-container.mobile-search-open {
        z-index: 9999;
      }

      /* Hide header on mobile when sidebar is open */
      .header-container.hidden-mobile {
        display: none;
      }

      .main-layout {
        flex: 1;
        overflow: hidden;
        padding: 0 15px; /* 3 * 4px = 12px spacing from Figma */
        position: relative;
      }

      .content-container {
        height: 100%;
        background-color: var(--colors-background-secondary);
        border-radius: 25px 25px 0px 0px; /* Figma specification */
        display: flex;
        min-width: 0;
        overflow: hidden;
        transition: var(--component-transition);
      }

      .sidebar-container {
        flex-shrink: 0;
        padding: 10px 0 10px 10px; /* 3 * 4px = 12px padding */
      }

      .main-content {
        flex: 1;
        min-width: 0;
        border-radius: 17px; /* Figma specification */
        overflow: hidden;
        margin-left: 0;
        transition: margin-left 0.3s ease-in-out;
        margin: 10px 20px 10px 10px;
        position: relative;
      }

      .content-area {
        padding: 0px;
        height: 100%;
        overflow-y: hidden; /* Allow vertical scrolling */
        overflow-x: hidden; /* Prevent horizontal scrolling */
        scroll-behavior: smooth;
        -webkit-overflow-scrolling: touch; /* Smooth scrolling on mobile */
        scrollbar-width: none; /* Firefox */
        -ms-overflow-style: none; /* Internet Explorer 10+ */
      }

      .content-area::-webkit-scrollbar {
        display: none; /* Hide scrollbar for modern browsers */
        width: 0px;
        height: 0px;
      }

      /* Mobile responsive behavior - Updated for better performance */
      @media (max-width: 767px) {
        .main-layout {
          padding: 0;
        }
        // .content-area {
        //   overflow-x: visible !important; /* Allow vertical scrolling on mobile */
        // }
        .content-container {
          border-radius: 40px 40px 0px 0px;
        }
        .header-container {
          flex-shrink: 0;
          position: relative;
          z-index: 0;
        }

        :host-context(body.settings-modal-open) .header-container {
          z-index: -1 !important;
        }

        .header-container.mobile-search-open {
          z-index: 9999;
        }
        .sidebar-container {
          position: fixed;
          z-index: 1001;
          top: 0;
          left: 0;
          bottom: 0;
          width: 100%;
          background-color: var(--colors-background-card);
          transform: translateX(-100%);
          transition: transform 0.3s ease-out;
          will-change: transform;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
        }

        .sidebar-container.open {
          transform: translateX(0);
        }

        .main-content {
          border-radius: 15px;
          margin: 25px 20px 0 20px !important;
        }

        /* Ensure mobile resizable panel can render fullscreen above dashboard container on iOS */
        :host-context(body.mobile-resizable-panel-open) .content-container {
          border-radius: 0 !important;
          overflow: visible !important;
        }

        :host-context(body.mobile-resizable-panel-open) .main-content {
          border-radius: 0 !important;
          margin: 0 !important;
          overflow: visible !important;
        }

        :host-context(body.mobile-resizable-panel-open) .content-area {
          overflow: visible !important;
          -webkit-overflow-scrolling: auto !important;
        }

        /* iOS fix: same overflow/clip reset for the More Info panel.
           Prevents iOS Safari from creating compositing layers (overflow:hidden +
           border-radius + -webkit-overflow-scrolling) that can render above the
           portaled position:fixed overlay regardless of z-index. */
        :host-context(body.more-info-panel-open) .content-container {
          border-radius: 0 !important;
          overflow: visible !important;
        }

        :host-context(body.more-info-panel-open) .main-content {
          border-radius: 0 !important;
          margin: 0 !important;
          overflow: visible !important;
        }

        :host-context(body.more-info-panel-open) .content-area {
          overflow: visible !important;
          -webkit-overflow-scrolling: auto !important;
        }

        .content-area {
          // padding: 12px;
        }

        .content-area::-webkit-scrollbar {
          display: none; /* Hide scrollbar for mobile */
        }
      }

      /* Tablet responsive behavior */
      @media (min-width: 769px) and (max-width: 1024px) {
        .main-layout {
          padding: 0 10px;
        }

        .content-container {
          border-radius: 22px 22px 0px 0px;
        }
      }

      /* Custom scrollbar for content area */
      .content-area::-webkit-scrollbar {
        width: 0px;
      }

      .content-area::-webkit-scrollbar-track {
        background: transparent;
      }

      .content-area::-webkit-scrollbar-thumb {
        background: var(--colors-border-muted);
        border-radius: 3px;
      }

      .content-area::-webkit-scrollbar-thumb:hover {
        background: var(--colors-border-primary);
      }

      /* Smooth transitions */
      * {
        transition:
          background-color 0.2s ease-in-out,
          border-color 0.2s ease-in-out;
      }

      /* Loading state overlay */
      .loading-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: var(--colors-background-overlay);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1001;
        transition: var(--component-transition);
      }
    `,
  ],
})
export class AppLayoutComponent {
  loadingService = inject(LoadingService);

  // Responsive state management using Angular signals
  sidebarOpen = signal(false);
  isMobile = signal(false);
  windowWidth = signal(0);
  mobileSearchOpen = signal(false);
  companySwitcherOpen = signal(false);

  constructor() {
    // Set initial responsive state based on screen size
    if (typeof window !== 'undefined') {
      this.updateResponsiveState();
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.updateResponsiveState();
  }

  toggleSidebar(): void {
    this.sidebarOpen.update(open => !open);
  }

  closeSidebar(): void {
    if (this.isMobile()) {
      this.sidebarOpen.set(false);
    }
  }

  private updateResponsiveState(): void {
    const width = window.innerWidth;
    this.windowWidth.set(width);
    this.isMobile.set(width < 768);

    // Auto-manage sidebar state based on screen size
    if (width < 768) {
      // Mobile: Sidebar closed by default
      this.sidebarOpen.set(false);
    } else {
      // Desktop/Tablet: Sidebar open by default
      this.sidebarOpen.set(true);
    }
  }
}
